import { useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { CurrentImageProps } from '@abyss/3d-viewer';
import { ResponsiveButton } from './styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useFeatureFlag } from '@/hooks';
import Tooltip from '@mui/material/Tooltip';

type SphericalWithPaintRegion = CurrentImageProps & {
  paintRegion?: string;
};

type ScanNameDisplayProps = {
  selectedLocation: SphericalWithPaintRegion;
};

export function ScanNameDisplay({ selectedLocation }: ScanNameDisplayProps) {
  const structureLocations = useAtomValue(state.structureLocations);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const mainRef = useRef(null);
  const isAlphaUser = useFeatureFlag('alpha-user');

  const locationData = useMemo(() => {
    for (const location of structureLocations ?? []) {
      if (location.id === selectedLocation.id) {
        return location;
      }
    }
    return undefined;
  }, [selectedLocation, structureLocations]);

  const handleMenuClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isAlphaUser || !locationData?.imageSensor) return;
    setAnchorElement(mainRef.current ?? undefined);
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const goToVideo = (
    videoPath: string | undefined | null,
    videoTimestamp?: number | null | undefined
  ) => {
    if (!videoPath) return;
    const videoUrl = getCloudfrontUrl(videoPath);
    const timestamp = videoTimestamp ? `#t=${videoTimestamp}` : '';
    window.open(videoUrl + timestamp, '_blank');
  };
  const formatTimestamp = (timestamp: number | null | undefined) => {
    if (!timestamp) return '';
    const minutes = Math.floor(timestamp / 60);
    const secs = Math.floor(timestamp % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };
  return (
    <>
      <Tooltip title="Deck / Paint Block / Scan Name">
        <ResponsiveButton
          variant="contained"
          className="responsive-btn"
          // sx={{ mr: 2, mb: 2 }}
          onClick={handleMenuClick}
          ref={mainRef}
        >
          <>{locationData?.deck?.name ?? ''}</>/
          <>{locationData?.paintRegion ? <>{locationData?.paintRegion.name}</> : ' - '}</>/
          <b>{selectedLocation?.name}</b>
        </ResponsiveButton>
      </Tooltip>
      <Menu
        id="lock-menu"
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'lock-button',
          role: 'listbox',
        }}
      >
        <MenuItem disabled>Image Metadata</MenuItem>
        <MenuItem>Width: {locationData?.imageSensor?.width}</MenuItem>
        <MenuItem>Height: {locationData?.imageSensor?.height}</MenuItem>
        {locationData?.videoResourcePath && (
          <MenuItem
            onClick={() => goToVideo(locationData?.videoResourcePath, locationData?.videoTimestamp)}
          >
            <u>
              Open Video{' '}
              {locationData?.videoTimestamp
                ? 'at ' + formatTimestamp(locationData?.videoTimestamp)
                : ''}
            </u>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
